import {
  FluidButtonType,
  FluidIconKey,
  FluidModalAction,
  FluidTheme,
  FluidThemeInterface,
  FluidTourStep,
} from '@lmig/fluid-core';
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
} from '@stencil/core';

import { Utils } from '../../utils/utils';

/**
 * @displayName Tour
 *
 */
@Component({
  tag: 'fluid-tour',
  styleUrl: 'fluid-tour.scss',
  shadow: true,
})
export class FluidTour implements FluidThemeInterface {
  private static readonly PREVIOUS_EVENT_KEY = 'previous';
  private static readonly NEXT_EVENT_KEY = 'next';
  private static readonly DONE_EVENT_KEY = 'done';

  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLElement;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   * @type {string}
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * Define the steps in the tour. Takes a list of objects with the following
   *
   * selector - a css selector to jump to for the step (required)
   * title - the title for the modal at that step
   * body - the body for the modal at that step
   * scrollOffset - an additional offset to scroll for this step
   * preStepAction - a function to run before coming to this step
   * postStepAction - a function to run after leaving this step
   *
   */
  @Prop({ mutable: true }) steps: FluidTourStep[];

  /**
   * Additional offset to use when scrolling to every step in the tour
   */
  @Prop({ mutable: true }) globalScrollOffset: number;

  // ================================================================ //
  // -- States

  @State() _currentStepNumber = 0;
  @State() _isTouring = false;

  @State() _previousAction: FluidModalAction = {
    label: 'Previous',
    type: FluidButtonType.SECONDARY,
    eventKey: FluidTour.PREVIOUS_EVENT_KEY,
    iconBefore: {
      key: FluidIconKey.CHEVRON_LEFT,
    },
  };
  @State() _nextAction: FluidModalAction = {
    label: 'Next',
    type: FluidButtonType.SECONDARY,
    eventKey: FluidTour.NEXT_EVENT_KEY,
    iconAfter: {
      key: FluidIconKey.CHEVRON_RIGHT,
    },
  };
  @State() _doneAction: FluidModalAction = {
    label: 'Done',
    type: FluidButtonType.PRIMARY,
    eventKey: FluidTour.DONE_EVENT_KEY,
  };
  @State() _actions = [this._previousAction, this._nextAction];

  // ================================================================ //
  // -- Events

  /**
   * Emits when the tour is stopped and at what step number (0 indexed).
   */
  @Event() tourStopped: EventEmitter<number>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: setZIndex - Set component z-index relative to global base value
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    Utils.setZIndex(this.host);
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Start the tour! Can be passed a step number to start at (0 indexed).
   * If not passed, will default to last open step. If no last open step, will default to 0.
   */
  @Method() async startTour(startingStepNumber?: number) {
    this._currentStepNumber = startingStepNumber ?? this._currentStepNumber;

    this.stepToElement(this.getElementAtStep(this.getCurrentStep()));

    this._previousAction.disabled = this._currentStepNumber === 0;
    this._actions = [
      this._previousAction,
      this._currentStepNumber < this.steps.length - 1
        ? this._nextAction
        : this._doneAction,
    ];

    this._isTouring = true;
  }

  /**
   * Stops the tour. Emits tourStopped at the current step number (0 indexed).
   */
  @Method() async stopTour() {
    const element = this.getElementAtStep(this.getCurrentStep());
    if (element) {
      this.stepAway(element);
    }
    this._isTouring = false;
    this.tourStopped.emit(this._currentStepNumber);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Configure any changes to the element necessary and scroll
   */
  stepToElement(element: HTMLElement) {
    if (typeof this.getCurrentStep().preStepAction === 'function') {
      this.getCurrentStep().preStepAction();
    }

    if (element) {
      if (element.style.position === '') {
        element.style.position = 'relative';
      }

      this.setElementStyle(element, 'z-index', `${this.zIndex * 10}`);
      this.setElementStyle(element, 'will-change', 'transform');

      // Defer scroll to right before next repaint in case there is anything in the preStepAction or postStepAction of prior step
      // that modify the screen (ex expanding a collapse).
      window.requestAnimationFrame(() => {
        const scrollLocation =
          element.getBoundingClientRect().top +
          window.pageYOffset +
          (this.getCurrentStep().scrollOffset ?? 0) +
          (this.globalScrollOffset ?? 0);
        window.scrollTo({ top: scrollLocation, behavior: 'smooth' });
      });
    }
  }

  /**
   * Clean up any changes to the element in the step of interest
   */
  stepAway(element: HTMLElement) {
    this.resetElementStyle(element, 'z-index');
    this.resetElementStyle(element, 'will-change');

    if (typeof this.getCurrentStep().postStepAction === 'function') {
      this.getCurrentStep().postStepAction();
    }
  }

  /**
   * Set the style on the element for touring it
   */
  setElementStyle(
    element: HTMLElement,
    styleName: string,
    styleValue: number | string
  ) {
    if (element) {
      element.setAttribute(
        this.getTourStyleAttributeName(styleName),
        JSON.parse(JSON.stringify(element.style[styleName]))
      );
      element.style[styleName] = styleValue;
    }
  }

  /**
   * Resets the style on the element to what it was prior to touring
   */
  resetElementStyle(element: HTMLElement, styleName: string) {
    if (element) {
      if (element.getAttribute(this.getTourStyleAttributeName(styleName))) {
        element.style[styleName] = element.getAttribute(
          this.getTourStyleAttributeName(styleName)
        );
      } else {
        element.style[styleName] = null;
      }

      element.removeAttribute(this.getTourStyleAttributeName(styleName));
    }
  }

  /**
   * Handles the actions on the tour modal (button clicked events)
   */
  handleAction(event: CustomEvent) {
    if (event.detail === FluidTour.PREVIOUS_EVENT_KEY) {
      this.stepAway(this.getElementAtStep(this.getCurrentStep()));
      this.startTour(this._currentStepNumber - 1);
    } else if (event.detail === FluidTour.NEXT_EVENT_KEY) {
      this.stepAway(this.getElementAtStep(this.getCurrentStep()));
      this.startTour(this._currentStepNumber + 1);
    } else {
      this.stopTour();
    }
  }

  /**
   * Returns the current step configuration
   */
  getCurrentStep(): FluidTourStep {
    return this.steps[this._currentStepNumber];
  }

  /**
   * Returns the element at the given step
   */
  getElementAtStep(step: FluidTourStep): HTMLElement {
    return document.querySelector(step?.selector);
  }

  /**
   * Returns the attribute name for storing the given style
   */
  getTourStyleAttributeName(styleName: string) {
    return `data-fluid-tour-prior-${styleName}`;
  }

  /**
   * Returns formatted title for current step
   */
  getCurrentTitle() {
    return `${this.getCurrentStep().title ?? ''} (${
      this._currentStepNumber + 1
    }/${this.steps.length})`;
  }

  /**
   * Returns formatted body for current step
   */
  getCurrentBody() {
    return this.getCurrentStep().body ?? '';
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div class={`fluid-tour theme-${this.theme}`}>
        <div
          style={{ 'z-index': `${this.zIndex}` }}
          class={{
            overlay: true,
            visible: this._isTouring,
            hidden: !this._isTouring,
          }}
        />
        <fluid-modal
          title={this.getCurrentTitle()}
          open={this._isTouring}
          onModalClosed={this.stopTour.bind(this)}
          modalActions={this._actions}
          onActionClicked={this.handleAction.bind(this)}
          outsideClickClose={false}
          transparentOverlay={true}
          theme={this.theme}
          style={{ 'z-index': `${this.zIndex * 100}` }}>
          {this.getCurrentBody()}
        </fluid-modal>
      </div>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Overrides default CSS z-index values for full screen overlay,
   * Modal, & highlighted element at current step.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;
}
